// Nine Lives '84 - procedural models.
//
// Every model is built from primitives in world units (the original's
// pixels), with its origin at the bottom-left of the matching simulation
// box so pictures line up with collision. Toon materials and hull outlines
// give the cartoon look.

import * as THREE from 'three';
import type { Alley, AlleyWindow } from '../sim/alley';
import {
  ARROW_H,
  ARROW_W,
  BIRD_W,
  BOWL_H,
  BOWL_W,
  BROOM_H,
  BROOM_W,
  CAGE_H,
  CAGE_W,
  CAT_W,
  DOG_W,
  EEL_H,
  EEL_W,
  FISH_H,
  FISH_W,
  GIFT_H,
  GIFT_W,
  HEART_H,
  HEART_W,
  JUNK_H,
  JUNK_W,
  MOUSE_H,
  MOUSE_W,
  SPIDER_H,
  SPIDER_W,
  WATER_SURFACE,
  WORLD_H,
  WORLD_W,
} from '../sim/constants';
import type { Room } from '../sim/room';
import type { BookshelfRoom } from '../sim/rooms/bookshelf';
import type { CheeseRoom } from '../sim/rooms/cheese';
import type { Box, Platform } from '../sim/types';
import { addOutlines, toon } from './materials';

const matCache = new Map<number, THREE.MeshToonMaterial>();
export function mat(color: number): THREE.MeshToonMaterial {
  let m = matCache.get(color);
  if (!m) {
    m = toon(color);
    matCache.set(color, m);
  }
  return m;
}

/** Depth of the back wall's face; the play plane is z = 0. */
export const WALL_Z = -36;

export const SHARED = {
  nose: mat(0xd94f8a),
  eye: new THREE.MeshBasicMaterial({ color: 0xffd166, toneMapped: false }),
  can: mat(0x8d949c),
  canLid: mat(0xb5bcc4),
  fence: mat(0x8a6a3c),
  fenceDark: mat(0x6a4e2a),
  rope: mat(0xd9cbb3),
  street: mat(0x3b3f45),
  kerb: mat(0x6a6f76),
  brick: mat(0x8a4a3a),
  brickDark: mat(0x6e3a2c),
  windowDark: mat(0x1a2230),
  windowLit: new THREE.MeshBasicMaterial({ color: 0xffe9a8, toneMapped: false }),
  glass: new THREE.MeshToonMaterial({
    color: 0x9fc4d8,
    transparent: true,
    opacity: 0.55,
  }),
  frame: mat(0xd9cbb3),
  dog: mat(0x7a4a2a),
  dogLight: mat(0xb08050),
  mouse: mat(0x9a9aa4),
  pot: mat(0xb8653a),
  leaf: mat(0x4f9a4a),
};

function box(
  w: number,
  h: number,
  d: number,
  m: THREE.Material,
  x = 0,
  y = 0,
  z = 0,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
  mesh.position.set(x, y, z);
  return mesh;
}

export interface CatModel {
  root: THREE.Group;
  /** Flipped by the view for facing; rotated for tumbles. */
  body: THREE.Group;
  legs: THREE.Mesh[];
  tail: THREE.Mesh;
  /** Meshes in the fur colour, for tinting (low on air). */
  fur: THREE.Mesh[];
  furMaterial: THREE.Material;
}

/** A cat facing +X, origin at the bottom-left of its CAT_W x 10 box. */
export function buildCat(fur = 0x3a3f4a, light = 0x8a8f9a): CatModel {
  const furM = mat(fur);
  const lightM = mat(light);
  const root = new THREE.Group();
  const body = new THREE.Group();
  body.position.set(CAT_W / 2, 0, 0);
  root.add(body);

  const torso = box(9, 5, 5, furM, -1, 5, 0);
  const head = box(5, 5, 5, furM, 4.5, 7.5, 0);
  const earL = box(1.5, 2, 1, furM, 3.5, 10.5, 1.5);
  const earR = box(1.5, 2, 1, furM, 3.5, 10.5, -1.5);
  const nose = box(1, 1, 1, SHARED.nose, 7.2, 7, 0);
  const eyeL = box(0.8, 0.8, 0.4, SHARED.eye, 6.6, 8.6, 1.4);
  const eyeR = box(0.8, 0.8, 0.4, SHARED.eye, 6.6, 8.6, -1.4);
  const chest = box(3, 3, 4.6, lightM, 2, 4.5, 0);
  const legs = [
    box(1.6, 3, 1.6, furM, -4, 1.5, 1.4),
    box(1.6, 3, 1.6, furM, -4, 1.5, -1.4),
    box(1.6, 3, 1.6, furM, 2.5, 1.5, 1.4),
    box(1.6, 3, 1.6, furM, 2.5, 1.5, -1.4),
  ];
  // The tail trails behind (-X) from a pivot at its base and curls upward:
  // a negative z rotation lifts a -X limb, so the views wag around -0.5.
  const tail = box(6, 1.4, 1.4, furM, 0, 0, 0);
  tail.geometry.translate(-3, 0, 0);
  tail.position.set(-5.5, 7, 0);
  tail.rotation.z = -0.5;
  body.add(torso, head, earL, earR, nose, eyeL, eyeR, chest, tail, ...legs);
  addOutlines(body, 0.35);
  return {
    root,
    body,
    legs,
    tail,
    fur: [torso, head, earL, earR, tail, ...legs],
    furMaterial: furM,
  };
}

/** The birdcage, origin bottom-left of its CAGE_W x CAGE_H box. */
export function buildCage(): THREE.Group {
  const g = new THREE.Group();
  const gold = mat(0xd9b24a);
  const r = CAGE_W / 2;
  const base = new THREE.Mesh(new THREE.CylinderGeometry(r, r, 2, 16), gold);
  base.position.set(r, 1, 0);
  const top = new THREE.Mesh(new THREE.CylinderGeometry(r, r, 1.2, 16), gold);
  top.position.set(r, CAGE_H - 6, 0);
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(r, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2),
    gold,
  );
  dome.position.set(r, CAGE_H - 6, 0);
  dome.scale.y = 0.5;
  g.add(base, top, dome);
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    const bar = box(0.7, CAGE_H - 7, 0.7, gold, r + Math.cos(a) * (r - 0.5), (CAGE_H - 6) / 2 + 1, Math.sin(a) * (r - 0.5));
    g.add(bar);
  }
  // A perch and the bird inside.
  g.add(box(r * 1.4, 0.8, 0.8, SHARED.fenceDark, r, 9, 0));
  const bird = buildBird(0xffd166);
  bird.root.position.set(r - BIRD_W / 2, 9.5, 0);
  g.add(bird.root);
  addOutlines(g, 0.25);
  return g;
}

/** The cage on its side, bars sprung, origin bottom-left of the same box. */
export function buildBrokenCage(): THREE.Group {
  const g = new THREE.Group();
  const gold = mat(0xd9b24a);
  const r = CAGE_W / 2;
  const ring = new THREE.Mesh(new THREE.TorusGeometry(r - 1, 0.8, 6, 16), gold);
  ring.position.set(r, r, 0);
  g.add(ring);
  for (let i = 0; i < 7; i++) {
    const bar = box(0.7, CAGE_H - 8, 0.7, gold, r + (i - 3) * 3, 6 + (i % 2) * 2, (i % 3) - 1);
    bar.rotation.z = 1.2 + (i % 3) * 0.3;
    g.add(bar);
  }
  addOutlines(g, 0.25);
  return g;
}

export interface BirdModel {
  root: THREE.Group;
  wings: THREE.Mesh[];
}

/** A small bird facing +X, origin bottom-left of its BIRD_W x BIRD_H box. */
export function buildBird(colour = 0x5ec4e8): BirdModel {
  const root = new THREE.Group();
  const m = mat(colour);
  const body = box(6, 4.5, 4, m, BIRD_W / 2, 3.5, 0);
  const head = box(3.5, 3.5, 3.5, m, BIRD_W / 2 + 3.5, 6, 0);
  const beak = box(2, 1, 1, mat(0xffa030), BIRD_W / 2 + 6, 5.8, 0);
  const eye = box(0.6, 0.6, 0.3, SHARED.eye, BIRD_W / 2 + 4.6, 6.8, 1.8);
  const tailF = box(3, 1, 2, m, BIRD_W / 2 - 4, 4, 0);
  const wings = [box(4, 0.6, 3.5, m, 0, 0, 0), box(4, 0.6, 3.5, m, 0, 0, 0)];
  wings[0].geometry.translate(0, 0, 1.75);
  wings[1].geometry.translate(0, 0, -1.75);
  wings[0].position.set(BIRD_W / 2, 5.5, 1.5);
  wings[1].position.set(BIRD_W / 2, 5.5, -1.5);
  root.add(body, head, beak, eye, tailF, ...wings);
  addOutlines(root, 0.25);
  return { root, wings };
}

export interface BowlModel {
  root: THREE.Group;
  milk: THREE.Mesh;
}

/** A milk bowl, origin bottom-left of its BOWL_W x BOWL_H box. */
export function buildBowl(): BowlModel {
  const root = new THREE.Group();
  const r = BOWL_W / 2;
  const bowl = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 0.7, BOWL_H, 16), mat(0x3a6ea8));
  bowl.position.set(r, BOWL_H / 2, 0);
  const milk = new THREE.Mesh(
    new THREE.CylinderGeometry(r - 1, r - 1, 1, 16),
    new THREE.MeshBasicMaterial({ color: 0xfaf6ea, toneMapped: false }),
  );
  milk.position.set(r, BOWL_H - 0.6, 0);
  root.add(bowl, milk);
  addOutlines(root, 0.3);
  return { root, milk };
}

/** The floating milk carton, origin bottom-left of a 12 x 18 box. */
export function buildCarton(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(12, 14, 8, mat(0xf4efe3), 6, 7, 0));
  const roof = box(12, 4, 8, mat(0x4a8ad0), 6, 16, 0);
  roof.scale.z = 0.6;
  g.add(roof);
  g.add(box(10, 3, 0.5, mat(0x4a8ad0), 6, 8, 4.3));
  addOutlines(g, 0.3);
  return g;
}

/** The fishbowl on the table in the fishbowl room. */
export function buildFishbowl(bowl: Box): THREE.Group {
  const g = new THREE.Group();
  const r = bowl.w / 2;
  const cx = bowl.x + r;
  const cy = bowl.y + r * 0.8;
  const glass = new THREE.MeshToonMaterial({
    color: 0xbfe6f5,
    transparent: true,
    opacity: 0.35,
  });
  const water = new THREE.MeshToonMaterial({
    color: 0x3a8fd0,
    transparent: true,
    opacity: 0.55,
  });
  const inner = new THREE.Mesh(new THREE.SphereGeometry(r - 2, 20, 14), water);
  inner.position.set(cx, cy - 2, 0);
  inner.scale.y = 0.85;
  const outer = new THREE.Mesh(new THREE.SphereGeometry(r, 20, 14), glass);
  outer.position.set(cx, cy, 0);
  const rim = new THREE.Mesh(new THREE.TorusGeometry(r * 0.7, 1.2, 6, 20), mat(0xbfe6f5));
  rim.rotation.x = Math.PI / 2;
  rim.position.set(cx, cy + r * 0.7, 0);
  const fish = buildFish(0xff8a3a);
  fish.position.set(cx - 6, cy, 6);
  g.add(inner, outer, rim, fish);
  return g;
}

/** A fish facing +X, origin bottom-left of its FISH_W x FISH_H box. */
export function buildFish(colour = 0xff8a3a): THREE.Group {
  const g = new THREE.Group();
  const m = mat(colour);
  const body = box(6, FISH_H, 3, m, FISH_W / 2 + 1, FISH_H / 2, 0);
  const tail = box(2.5, FISH_H * 0.9, 1, m, 1, FISH_H / 2, 0);
  tail.rotation.z = 0.2;
  const eye = box(0.7, 0.7, 0.3, SHARED.eye, FISH_W / 2 + 3, FISH_H * 0.65, 1.6);
  g.add(body, tail, eye);
  addOutlines(g, 0.2);
  return g;
}

export interface EelModel {
  root: THREE.Group;
  segments: THREE.Mesh[];
}

/** An eel facing +X in three segments, origin bottom-left of EEL_W x EEL_H. */
export function buildEel(): EelModel {
  const root = new THREE.Group();
  const m = mat(0x6a7a3a);
  const band = mat(0xf0e040);
  const segments: THREE.Mesh[] = [];
  const segW = EEL_W / 3;
  for (let i = 0; i < 3; i++) {
    const s = box(segW - 0.5, EEL_H - i * 0.8, 3.5, m, segW / 2 + i * segW, EEL_H / 2, 0);
    segments.push(s);
    root.add(s);
    if (i < 2) root.add(box(1, EEL_H - i * 0.8 + 0.4, 3.7, band, segW * (i + 1) - 1.5, EEL_H / 2, 0));
  }
  const eye = box(0.8, 0.8, 0.3, SHARED.eye, EEL_W - 3, EEL_H * 0.65, 1.9);
  root.add(eye);
  addOutlines(root, 0.25);
  return { root, segments };
}

/** The inside of the tank: water, sand, weed, the glass at the back. */
export function buildTank(): THREE.Group {
  const g = new THREE.Group();
  const water = new THREE.MeshToonMaterial({
    color: 0x2a78c8,
    transparent: true,
    opacity: 0.45,
    depthWrite: false,
  });
  g.add(box(WORLD_W + 80, WATER_SURFACE, 60, water, WORLD_W / 2, WATER_SURFACE / 2, -14));
  g.add(box(WORLD_W + 80, 1.2, 60, mat(0xbfe6f5), WORLD_W / 2, WATER_SURFACE, -14));
  g.add(box(WORLD_W + 80, WORLD_H + 40, 4, mat(0x10305a), WORLD_W / 2, WORLD_H / 2, WALL_Z - 6));
  g.add(box(WORLD_W + 80, 6, 90, mat(0xd9c48a), WORLD_W / 2, -3, 10));
  const weed = mat(0x3a9a4a);
  for (const [x, h] of [
    [30, 40],
    [48, 28],
    [150, 34],
    [262, 46],
    [284, 30],
  ]) {
    for (let i = 0; i < 3; i++) {
      const blade = box(2.2, h - i * 6, 1.5, weed, x + i * 4 - 4, (h - i * 6) / 2, -22 + i * 3);
      blade.rotation.z = (i - 1) * 0.12;
      g.add(blade);
    }
  }
  // A little sunken castle.
  const stone = mat(0x8a8fa0);
  g.add(box(30, 22, 14, stone, 205, 11, -26));
  g.add(box(8, 34, 8, stone, 197, 17, -24));
  g.add(box(8, 34, 8, stone, 213, 17, -24));
  g.add(box(6, 9, 6, mat(0x1a2230), 205, 5, -19));
  addOutlines(g, 0.5);
  return g;
}

export interface DogModel {
  root: THREE.Group;
  body: THREE.Group;
  legs: THREE.Mesh[];
  jaw: THREE.Mesh;
}

/** The dog facing +X, origin at the bottom-left of its DOG_W x DOG_H box. */
export function buildDog(): DogModel {
  const root = new THREE.Group();
  const body = new THREE.Group();
  body.position.set(DOG_W / 2, 0, 0);
  root.add(body);
  const torso = box(15, 6, 6, SHARED.dog, -2, 6, 0);
  const head = box(7, 6, 6, SHARED.dog, 8, 8, 0);
  const snout = box(4, 3, 4, SHARED.dogLight, 12.5, 7, 0);
  const jaw = box(4, 1.2, 3.6, SHARED.dogLight, 12.5, 5.6, 0);
  const earL = box(2, 3, 1, SHARED.dog, 6.5, 11, 2.5);
  const earR = box(2, 3, 1, SHARED.dog, 6.5, 11, -2.5);
  const eyeL = box(0.9, 0.9, 0.4, SHARED.eye, 10.5, 9.5, 2.6);
  const eyeR = box(0.9, 0.9, 0.4, SHARED.eye, 10.5, 9.5, -2.6);
  const legs = [
    box(2, 4, 2, SHARED.dog, -7, 2, 1.8),
    box(2, 4, 2, SHARED.dog, -7, 2, -1.8),
    box(2, 4, 2, SHARED.dog, 3, 2, 1.8),
    box(2, 4, 2, SHARED.dog, 3, 2, -1.8),
  ];
  const tail = box(5, 1.4, 1.4, SHARED.dog, 0, 0, 0);
  tail.geometry.translate(-2.5, 0, 0);
  tail.position.set(-9, 9, 0);
  tail.rotation.z = -0.6;
  body.add(torso, head, snout, jaw, earL, earR, eyeL, eyeR, tail, ...legs);
  addOutlines(body, 0.4);
  return { root, body, legs, jaw };
}

/** A mouse facing +X, origin bottom-left of its MOUSE_W x MOUSE_H box. */
export function buildMouse(): THREE.Group {
  const g = new THREE.Group();
  const body = box(MOUSE_W - 1, MOUSE_H, 3, SHARED.mouse, (MOUSE_W - 1) / 2, MOUSE_H / 2, 0);
  const ear = box(1.2, 1.2, 0.6, SHARED.nose, MOUSE_W - 2, MOUSE_H + 0.3, 0);
  const tail = box(4, 0.5, 0.5, SHARED.mouse, -2, 1.5, 0);
  g.add(body, ear, tail);
  addOutlines(g, 0.25);
  return g;
}

/** A flowerpot, origin bottom-left of its JUNK_W x JUNK_H box. */
export function buildJunk(): THREE.Group {
  const g = new THREE.Group();
  const pot = new THREE.Mesh(
    new THREE.CylinderGeometry(JUNK_W / 2, JUNK_W / 2.6, JUNK_H * 0.7, 10),
    SHARED.pot,
  );
  pot.position.set(JUNK_W / 2, JUNK_H * 0.35, 0);
  const leaf = box(4, 2.5, 2.5, SHARED.leaf, JUNK_W / 2, JUNK_H * 0.85, 0);
  g.add(pot, leaf);
  addOutlines(g, 0.3);
  return g;
}

export interface WindowModel {
  root: THREE.Group;
  interior: THREE.Mesh;
  sash: THREE.Mesh;
  /** 0 closed .. 1 open, eased by the view. */
  openness: number;
  closedY: number;
  travel: number;
}

/** A sash window in the back wall with a sill that reaches the play plane. */
export function buildWindow(w: AlleyWindow): WindowModel {
  const root = new THREE.Group();
  const cx = w.x + w.w / 2;
  const cy = w.y + w.h / 2;
  const interior = box(w.w - 2, w.h - 2, 2, SHARED.windowDark, cx, cy, WALL_Z + 0.5);
  // Frame: two uprights, a lintel, and the sill (which is the platform).
  root.add(interior);
  root.add(box(2, w.h + 2, 3, SHARED.frame, w.x, cy, WALL_Z + 1.2));
  root.add(box(2, w.h + 2, 3, SHARED.frame, w.x + w.w, cy, WALL_Z + 1.2));
  root.add(box(w.w + 2, 2, 3, SHARED.frame, cx, w.y + w.h, WALL_Z + 1.2));
  const sill = w.sill;
  root.add(
    box(sill.w + 4, sill.h, -WALL_Z + 2, SHARED.frame, cx, sill.y + sill.h / 2, WALL_Z / 2 + 1),
  );
  // Lower sash: slides up when the window opens.
  const sashH = w.h * 0.52;
  const closedY = w.y + sashH / 2;
  const sash = box(w.w - 1, sashH, 1, SHARED.glass, cx, closedY, WALL_Z + 2.2);
  const bar = box(w.w - 1, 1.2, 1.4, SHARED.frame, 0, 0, 0);
  bar.position.set(0, sashH / 2, 0.2);
  sash.add(bar);
  root.add(sash);
  return { root, interior, sash, openness: 0, closedY, travel: w.h * 0.42 };
}

export interface BroomModel {
  root: THREE.Group;
  bristles: THREE.Mesh;
}

/** The broom, upright, origin bottom-left of its BROOM_W x BROOM_H box. */
export function buildBroom(): BroomModel {
  const root = new THREE.Group();
  const stick = box(1.6, BROOM_H - 8, 1.6, SHARED.fence, BROOM_W / 2, (BROOM_H - 8) / 2 + 8, 0);
  const bristles = box(BROOM_W, 8, 4, mat(0xd9b46a), BROOM_W / 2, 4, 0);
  const band = box(BROOM_W - 2, 1.5, 4.4, mat(0x9a3a3a), BROOM_W / 2, 8, 0);
  root.add(stick, bristles, band);
  addOutlines(root, 0.3);
  return { root, bristles };
}

/**
 * A pawprint: four toes and a pad. The camera looks almost along the
 * floor, so the print is tilted up toward it rather than lying flat, and
 * the room views set it a little above any rug.
 */
export function buildPawprint(): THREE.Group {
  const g = new THREE.Group();
  const m = new THREE.MeshBasicMaterial({ color: 0x1e1612 });
  g.add(box(4.5, 3.5, 0.4, m, 0, 0, 0));
  for (let i = 0; i < 4; i++) {
    g.add(box(1.4, 1.4, 0.4, m, -2.6 + i * 1.75, 2.9, 0));
  }
  g.rotation.x = -0.55;
  return g;
}

/** A fern in a pot, origin bottom-left of its 10 x 12 box. */
export function buildFern(): THREE.Group {
  const g = new THREE.Group();
  const pot = new THREE.Mesh(new THREE.CylinderGeometry(4, 3, 5, 10), SHARED.pot);
  pot.position.set(5, 2.5, 0);
  g.add(pot);
  for (let i = 0; i < 5; i++) {
    const leaf = box(2, 7, 1.2, SHARED.leaf, 5, 8, 0);
    leaf.rotation.z = -0.8 + i * 0.4;
    leaf.position.x = 5 + (i - 2) * 1.5;
    g.add(leaf);
  }
  addOutlines(g, 0.3);
  return g;
}

export interface SpiderModel {
  root: THREE.Group;
  thread: THREE.Mesh;
  legs: THREE.Mesh[];
}

/** The spider, origin bottom-left of its SPIDER_W x SPIDER_H box. */
export function buildSpider(): SpiderModel {
  const root = new THREE.Group();
  const bodyM = mat(0x2b2230);
  const body = box(8, 6, 6, bodyM, SPIDER_W / 2, 5, 0);
  const head = box(4, 4, 4, bodyM, SPIDER_W / 2 + 5, 4.5, 0);
  const eyeL = box(0.9, 0.9, 0.4, new THREE.MeshBasicMaterial({ color: 0xff3355, toneMapped: false }), SPIDER_W / 2 + 6, 5.5, 1.5);
  const eyeR = eyeL.clone();
  eyeR.position.z = -1.5;
  const legs: THREE.Mesh[] = [];
  for (let i = 0; i < 4; i++) {
    for (const side of [-1, 1]) {
      const leg = box(1, 7, 1, bodyM, 0, 0, 0);
      leg.geometry.translate(0, -3, 0);
      leg.position.set(SPIDER_W / 2 - 3 + i * 2, 6, side * 3.5);
      leg.rotation.x = side * 0.9;
      leg.rotation.z = (i - 1.5) * 0.3;
      legs.push(leg);
      root.add(leg);
    }
  }
  const thread = box(0.5, 1, 0.5, mat(0xe8e8e8), SPIDER_W / 2, 0, 0);
  thread.geometry.translate(0, 0.5, 0); // grows upward from the body
  thread.position.y = SPIDER_H;
  root.add(body, head, eyeL, eyeR, thread);
  addOutlines(root, 0.3);
  return { root, thread, legs };
}

/** A mouse peeking out of a cheese hole toward the viewer: head and paws. */
export function buildPeekingMouse(): THREE.Group {
  const g = new THREE.Group();
  const head = box(6, 5, 5, SHARED.mouse, 0, 0, 2.5);
  const nose = box(1.4, 1.4, 1.4, SHARED.nose, 0, -0.8, 5.2);
  const earL = box(2.2, 2.2, 0.8, SHARED.nose, -2.6, 3, 1.5);
  const earR = box(2.2, 2.2, 0.8, SHARED.nose, 2.6, 3, 1.5);
  const eyeL = box(0.9, 0.9, 0.4, SHARED.eye, -1.5, 0.8, 5.1);
  const eyeR = box(0.9, 0.9, 0.4, SHARED.eye, 1.5, 0.8, 5.1);
  const pawL = box(1.6, 1.2, 2, SHARED.mouse, -2.5, -3, 3);
  const pawR = box(1.6, 1.2, 2, SHARED.mouse, 2.5, -3, 3);
  g.add(head, nose, earL, earR, eyeL, eyeR, pawL, pawR);
  addOutlines(g, 0.3);
  return g;
}

/** The room shell: floor, walls, ceiling, exit window and stool. */
export function buildRoomShell(room: Room, wallColour: number, floorColour: number): THREE.Group {
  const g = new THREE.Group();
  g.add(box(WORLD_W + 80, 6, 90, mat(floorColour), WORLD_W / 2, -3, 10));
  g.add(box(WORLD_W + 80, WORLD_H + 40, 8, mat(wallColour), WORLD_W / 2, WORLD_H / 2, WALL_Z - 4));
  // Skirting and a picture rail.
  g.add(box(WORLD_W + 80, 4, 2, SHARED.frame, WORLD_W / 2, 2, WALL_Z + 1));
  g.add(box(WORLD_W + 80, 1.5, 1.5, SHARED.frame, WORLD_W / 2, 160, WALL_Z + 0.8));
  // Side walls sit just outside the play area so Tabs stops at the edges.
  g.add(box(8, WORLD_H + 40, 60, mat(wallColour), -4, WORLD_H / 2, WALL_Z / 2));
  g.add(box(8, WORLD_H + 40, 60, mat(wallColour), WORLD_W + 4, WORLD_H / 2, WALL_Z / 2));
  // The exit window in the left wall: an opening showing night sky, framed.
  const w = room.window;
  g.add(box(2, w.h + 4, 20, mat(0x141a24), 0, w.y + w.h / 2, -8));
  g.add(box(3, 2, 22, SHARED.frame, 0, w.y + w.h + 1, -8));
  g.add(box(3, 2, 22, SHARED.frame, 0, w.y - 1, -8));
  const s = room.sill;
  g.add(box(s.w, s.h, 24, SHARED.frame, s.x + s.w / 2, s.y + s.h / 2, -6));
  // The stool beneath it.
  const st = room.stool;
  const stTop = st.y + st.h;
  g.add(box(st.w, 3, 14, SHARED.fence, st.x + st.w / 2, stTop - 1.5, 0));
  for (const dx of [3, st.w - 3]) {
    for (const dz of [-5, 5]) {
      g.add(box(2, stTop - 3, 2, SHARED.fenceDark, st.x + dx, (stTop - 3) / 2, dz));
    }
  }
  addOutlines(g, 0.6);
  return g;
}

/** Where the cheese's front face sits: just behind the play plane. */
export const CHEESE_FACE_Z = -3;

/** The table and the cheese wedge on it, pocked with holes. */
export function buildCheese(room: CheeseRoom): THREE.Group {
  const g = new THREE.Group();
  const cheese = mat(0xf2c744);
  const rind = mat(0xd9a92e);
  const depth = 30 - CHEESE_FACE_Z;
  const zc = CHEESE_FACE_Z - depth / 2;
  for (const s of room.steps) {
    g.add(box(s.w, s.h, depth, cheese, s.x + s.w / 2, s.y + s.h / 2, zc));
    g.add(box(s.w, 1.2, depth + 0.6, rind, s.x + s.w / 2, s.y + s.h - 0.6, zc));
  }
  // The table: a slab Tabs can jump onto from below, on four legs.
  const t = room.table;
  g.add(box(t.w, t.h, 44, SHARED.fence, t.x + t.w / 2, t.y + t.h / 2, -8));
  for (const dx of [5, t.w - 5]) {
    for (const dz of [-24, 12]) {
      g.add(box(3, t.y, 3, SHARED.fenceDark, t.x + dx, t.y / 2, dz));
    }
  }
  // The camera looks straight at the front face, so every hole is a dark
  // disc on it; the mouse pokes out of that same disc.
  const holeM = new THREE.MeshBasicMaterial({ color: 0x3a2508 });
  const rimM = mat(0xc9961f);
  for (const h of room.holes) {
    const rim = new THREE.Mesh(new THREE.CylinderGeometry(7.5, 7.5, 1, 16), rimM);
    rim.rotation.x = Math.PI / 2;
    rim.position.set(h.x, h.y, CHEESE_FACE_Z + 0.3);
    const disc = new THREE.Mesh(new THREE.CylinderGeometry(6, 6, 1, 16), holeM);
    disc.rotation.x = Math.PI / 2;
    disc.position.set(h.x, h.y, CHEESE_FACE_Z + 0.7);
    g.add(rim, disc);
  }
  for (const [x, y] of [
    [200, 50],
    [270, 100],
    [180, 80],
  ]) {
    const disc = new THREE.Mesh(new THREE.CylinderGeometry(3.5, 3.5, 1, 10), holeM);
    disc.rotation.x = Math.PI / 2;
    disc.position.set(x, y, CHEESE_FACE_Z + 0.6);
    g.add(disc);
  }
  addOutlines(g, 0.6);
  return g;
}

let heartGeometry: THREE.ExtrudeGeometry | null = null;

/** A heart shape HEART_W wide, its lobes' tops at y = 0, hanging below. */
function heartShapeGeometry(): THREE.ExtrudeGeometry {
  if (heartGeometry) return heartGeometry;
  const s = new THREE.Shape();
  // The classic three.js heart, drawn point-up then flipped to hang from its lobes.
  s.moveTo(25, 25);
  s.bezierCurveTo(25, 25, 20, 0, 0, 0);
  s.bezierCurveTo(-30, 0, -30, 35, -30, 35);
  s.bezierCurveTo(-30, 55, -10, 77, 25, 95);
  s.bezierCurveTo(60, 77, 80, 55, 80, 35);
  s.bezierCurveTo(80, 35, 80, 0, 50, 0);
  s.bezierCurveTo(35, 0, 25, 25, 25, 25);
  const geo = new THREE.ExtrudeGeometry(s, { depth: 6, bevelEnabled: false });
  // The shape spans x -30..80 (110 wide) and y 0..95 with the lobes at y = 0
  // and the tip at y = 95. Scale it to HEART_W wide, then turn it over (a
  // rotation, not a negative scale, so the faces keep their winding) so the
  // lobes are on top at y = 0 and the tip hangs below; centre it on x and z.
  const k = HEART_W / 110;
  geo.scale(k, k * 0.75, 1);
  geo.rotateX(Math.PI);
  geo.translate(-(25 * k), 0, 3);
  geo.computeVertexNormals();
  heartGeometry = geo;
  return geo;
}

export interface HeartModel {
  root: THREE.Group;
  whole: THREE.Mesh;
  broken: THREE.Group;
}

/** A heart platform, origin at its box's bottom-left; lobes at the top. */
export function buildHeart(): HeartModel {
  const root = new THREE.Group();
  const geo = heartShapeGeometry();
  const whole = new THREE.Mesh(geo, mat(0xe0406a));
  whole.position.set(HEART_W / 2, HEART_H, 0);
  const broken = new THREE.Group();
  const grey = mat(0x7a6a70);
  const left = new THREE.Mesh(geo, grey);
  left.position.set(HEART_W / 2 - 1.5, HEART_H - 1, 0);
  left.rotation.z = 0.12;
  const right = new THREE.Mesh(geo, grey);
  right.position.set(HEART_W / 2 + 1.5, HEART_H - 1, 0);
  right.rotation.z = -0.12;
  const crack = box(1.2, 18, 7, new THREE.MeshBasicMaterial({ color: 0x2a1a20 }), HEART_W / 2, HEART_H - 9, 0);
  broken.add(left, right, crack);
  broken.visible = false;
  root.add(whole, broken);
  addOutlines(root, 0.3);
  return { root, whole, broken };
}

/** Clementine: a white cat with a pink bow. */
export function buildFelicia(): CatModel {
  const m = buildCat(0xf4efe3, 0xffd0e0);
  const bow = mat(0xff6aa8);
  const l = box(2.2, 1.6, 1.2, bow, 3, 11, 1.2);
  const r = box(2.2, 1.6, 1.2, bow, 3, 11, -1.2);
  l.rotation.z = 0.5;
  r.rotation.z = -0.5;
  m.body.add(l, r, box(1, 1, 1, bow, 3, 11, 0));
  return m;
}

export interface CupidModel {
  root: THREE.Group;
  wings: THREE.Mesh[];
}

/** A cherub with a bow, origin bottom-left of a 12 x 14 box. */
export function buildCupid(): CupidModel {
  const root = new THREE.Group();
  const skin = mat(0xf2c9a8);
  const cloth = mat(0xf4efe3);
  const body = box(6, 6, 5, cloth, 6, 4, 0);
  const head = box(5, 5, 5, skin, 6, 9.5, 0);
  const hair = box(5.4, 1.6, 5.4, mat(0xe8c060), 6, 12.4, 0);
  const eyeL = box(0.7, 0.7, 0.3, SHARED.eye, 7.5, 10, 2.6);
  const eyeR = box(0.7, 0.7, 0.3, SHARED.eye, 4.5, 10, 2.6);
  const wings = [box(5, 3, 0.6, cloth, 0, 0, 0), box(5, 3, 0.6, cloth, 0, 0, 0)];
  wings[0].geometry.translate(-2.5, 0, 0);
  wings[1].geometry.translate(2.5, 0, 0);
  wings[0].position.set(3.2, 6, -2);
  wings[1].position.set(8.8, 6, -2);
  const bow = new THREE.Mesh(new THREE.TorusGeometry(3.5, 0.4, 6, 12, Math.PI), mat(0xb08040));
  bow.rotation.z = -Math.PI / 2;
  bow.position.set(11, 5, 2);
  root.add(body, head, hair, eyeL, eyeR, ...wings, bow);
  addOutlines(root, 0.25);
  return { root, wings };
}

/** An arrow with a heart-shaped tip, facing +X, origin bottom-left of ARROW_W x ARROW_H. */
export function buildArrow(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(ARROW_W - 2, 0.8, 0.8, mat(0xf4efe3), (ARROW_W - 2) / 2, ARROW_H / 2, 0));
  g.add(box(2.2, 2.2, 1.2, mat(0xe0406a), ARROW_W - 1, ARROW_H / 2, 0));
  const fletch = box(2, 2, 0.6, mat(0xe0406a), 1, ARROW_H / 2, 0);
  fletch.rotation.z = Math.PI / 4;
  g.add(fletch);
  return g;
}

/** A gift box with a ribbon, origin bottom-left of GIFT_W x GIFT_H. */
export function buildGift(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(GIFT_W, GIFT_H - 1, GIFT_W, mat(0x4a8ad0), GIFT_W / 2, (GIFT_H - 1) / 2, 0));
  const ribbon = mat(0xffd166);
  g.add(box(1.4, GIFT_H - 0.6, GIFT_W + 0.4, ribbon, GIFT_W / 2, (GIFT_H - 1) / 2, 0));
  g.add(box(GIFT_W + 0.4, GIFT_H - 0.6, 1.4, ribbon, GIFT_W / 2, (GIFT_H - 1) / 2, 0));
  g.add(box(3, 1.6, 1.6, ribbon, GIFT_W / 2, GIFT_H - 0.2, 0));
  addOutlines(g, 0.25);
  return g;
}

/** Clementine's room: pink walls with hearts, a carpet, her ledge; no window. */
export function buildFeliciaShell(ledge: Platform): THREE.Group {
  const g = new THREE.Group();
  g.add(box(WORLD_W + 80, 6, 90, mat(0x8a2a4a), WORLD_W / 2, -3, 10));
  g.add(box(WORLD_W + 80, WORLD_H + 40, 8, mat(0xf0b8c8), WORLD_W / 2, WORLD_H / 2, WALL_Z - 4));
  g.add(box(8, WORLD_H + 40, 60, mat(0xf0b8c8), -4, WORLD_H / 2, WALL_Z / 2));
  g.add(box(8, WORLD_H + 40, 60, mat(0xf0b8c8), WORLD_W + 4, WORLD_H / 2, WALL_Z / 2));
  // A sparse wallpaper of small hearts, flat on the back wall.
  const paper = mat(0xe8a0b8);
  let row = 0;
  for (let y = 40; y < WORLD_H + 20; y += 60, row++) {
    for (let x = row % 2 ? 60 : 30; x < WORLD_W; x += 64) {
      const h = new THREE.Mesh(heartShapeGeometry(), paper);
      h.scale.set(0.3, 0.3, 0.1);
      h.position.set(x, y, WALL_Z + 0.4);
      g.add(h);
    }
  }
  // Her ledge: a cushioned shelf with a valance.
  g.add(box(ledge.w, ledge.h, 24, mat(0xd94f8a), ledge.x + ledge.w / 2, ledge.y + ledge.h / 2, -8));
  g.add(box(ledge.w, 5, 1, mat(0xffd166), ledge.x + ledge.w / 2, ledge.y - 2, 4));
  addOutlines(g, 0.5);
  return g;
}

/** A plain wooden table for a room: its platform is the top slab; legs reach the floor. */
export function buildTable(t: Platform, depth = 30): THREE.Group {
  const g = new THREE.Group();
  const top = t.y + t.h;
  g.add(box(t.w, 3, depth, SHARED.fence, t.x + t.w / 2, top - 1.5, -depth / 2 + 8));
  for (const dx of [4, t.w - 4]) {
    for (const dz of [-depth + 12, 4]) {
      g.add(box(2.5, top - 3, 2.5, SHARED.fenceDark, t.x + dx, (top - 3) / 2, dz));
    }
  }
  addOutlines(g, 0.5);
  return g;
}

/** The bookcase with rows of books, and the table by the window. */
export function buildBookcase(room: BookshelfRoom): THREE.Group {
  const g = new THREE.Group();
  const wood = SHARED.fence;
  const dark = SHARED.fenceDark;
  const first = room.shelves[0];
  const last = room.shelves[room.shelves.length - 1];
  const x0 = first.x;
  const x1 = first.x + first.w;
  const top = last.y + last.h + 6;
  g.add(box(3, top, 22, dark, x0 - 1.5, top / 2, -12));
  g.add(box(3, top, 22, dark, x1 + 1.5, top / 2, -12));
  g.add(box(x1 - x0 + 6, top, 2, mat(0x4a3520), (x0 + x1) / 2, top / 2, -22));
  g.add(box(x1 - x0 + 6, 3, 24, wood, (x0 + x1) / 2, top - 1.5, -11));
  const bookColours = [0x9a3a3a, 0x3a5a9a, 0x4a8a4a, 0xc9a24a, 0x6a3a8a, 0xd9d0b8];
  let n = 0;
  for (const s of room.shelves) {
    g.add(box(s.w, s.h, 24, wood, s.x + s.w / 2, s.y + s.h / 2, -11));
    if (s === last) continue;
    let x = s.x + 4;
    while (x < s.x + s.w - 6) {
      const bw = 3 + ((n * 7) % 4);
      const bh = 14 + ((n * 5) % 9);
      g.add(box(bw, bh, 12, mat(bookColours[n % bookColours.length]), x + bw / 2, s.y + s.h + bh / 2, -14));
      x += bw + 0.8;
      n++;
    }
  }
  const t = room.table;
  const tTop = t.y + t.h;
  g.add(box(t.w, 3, 18, wood, t.x + t.w / 2, tTop - 1.5, 0));
  for (const dx of [4, t.w - 4]) {
    for (const dz of [-6, 6]) {
      g.add(box(2.5, tTop - 3, 2.5, dark, t.x + dx, (tTop - 3) / 2, dz));
    }
  }
  addOutlines(g, 0.6);
  return g;
}

/** The alley: street, back wall, cans, fence, clotheslines. Windows are separate. */
export function buildAlley(alley: Alley): THREE.Group {
  const g = new THREE.Group();

  // Street slab and kerb; the top face is y = 0.
  g.add(box(WORLD_W + 200, 6, 90, SHARED.street, WORLD_W / 2, -3, 10));
  g.add(box(WORLD_W + 200, 2, 4, SHARED.kerb, WORLD_W / 2, -1, WALL_Z + 3));

  // Back wall, set behind the play plane, with a few darker course lines.
  g.add(box(WORLD_W + 200, WORLD_H + 80, 8, SHARED.brick, WORLD_W / 2, WORLD_H / 2 + 20, WALL_Z - 4));
  for (let y = 12; y < WORLD_H + 40; y += 16) {
    g.add(box(WORLD_W + 200, 0.8, 0.6, SHARED.brickDark, WORLD_W / 2, y, WALL_Z + 0.2));
  }

  for (const c of alley.cans) g.add(buildCan(c));
  g.add(buildFence(alley.fence));
  for (const l of alley.lines) g.add(buildLine(l));
  addOutlines(g, 0.6);
  return g;
}

function buildCan(p: Platform): THREE.Object3D {
  const grp = new THREE.Group();
  const r = p.w / 2;
  const body = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 0.92, p.h - 2, 16), SHARED.can);
  body.position.set(p.x + r, p.y + (p.h - 2) / 2, 0);
  const lid = new THREE.Mesh(new THREE.CylinderGeometry(r + 0.8, r + 0.8, 2, 16), SHARED.canLid);
  lid.position.set(p.x + r, p.y + p.h - 1, 0);
  grp.add(body, lid);
  return grp;
}

/** The fence stands behind the cans so its posts never pass through them. */
const FENCE_Z = -16;

/** A picket fence: slats with pointed caps up to the platform top, two rails behind. */
function buildFence(p: Platform): THREE.Object3D {
  const grp = new THREE.Group();
  const top = p.y + p.h;
  const slatH = top - 2;
  const tones = [mat(0x8a6a3c), mat(0x93713f), mat(0x82633a), mat(0x8e6c3d)];
  let i = 0;
  for (let x = p.x + 3; x < p.x + p.w; x += 6, i++) {
    const m = tones[i % tones.length];
    grp.add(box(4, slatH, 1.6, m, x, slatH / 2, FENCE_Z));
    const cap = box(2.9, 2.9, 1.6, m, x, slatH, FENCE_Z);
    cap.rotation.z = Math.PI / 4;
    grp.add(cap);
  }
  for (const ry of [18, top - 16]) {
    grp.add(box(p.w, 3, 1.6, SHARED.fenceDark, p.x + p.w / 2, ry, FENCE_Z - 1.8));
  }
  for (let x = p.x + 12; x < p.x + p.w; x += 72) {
    grp.add(box(4, top - 4, 3, SHARED.fenceDark, x, (top - 4) / 2, FENCE_Z - 3));
  }
  return grp;
}

/** The rope of a clothesline with its pulley wheels; the laundry is drawn by the views. */
function buildLine(p: Platform): THREE.Object3D {
  const grp = new THREE.Group();
  const rope = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.7, p.w, 6), SHARED.rope);
  rope.rotation.z = Math.PI / 2;
  rope.position.set(p.x + p.w / 2, p.y + p.h / 2, 0);
  grp.add(rope);
  for (const x of [p.x - 3, p.x + p.w + 3]) {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(3.5, 3.5, 1.6, 12), SHARED.canLid);
    wheel.rotation.x = Math.PI / 2;
    wheel.position.set(x, p.y + p.h / 2, 0);
    grp.add(wheel);
  }
  return grp;
}

const LAUNDRY_COLOURS = [0xf4efe3, 0x4a8ad0, 0xd94f4a, 0xffd166, 0x9ad04a, 0xe8a0b8, 0x8ad0e0];

/** One garment hanging from a peg at its origin (the peg on the line). */
export function buildGarment(kind: number, colour: number): THREE.Group {
  const g = new THREE.Group();
  const m = mat(colour);
  const peg = mat(0xb08050);
  switch (kind % 4) {
    case 0: // a shirt: body, two sleeves, a peg at each shoulder
      g.add(box(9, 9, 1, m, 0, -5.5, -0.5));
      g.add(box(3, 3.5, 1, m, -5.5, -2.5, -0.5));
      g.add(box(3, 3.5, 1, m, 5.5, -2.5, -0.5));
      g.add(box(1.2, 2.4, 1.6, peg, -3.5, 0.4, -0.3));
      g.add(box(1.2, 2.4, 1.6, peg, 3.5, 0.4, -0.3));
      break;
    case 1: // a pair of socks on one peg
      g.add(box(3, 7, 1, m, -2.2, -4, -0.5));
      g.add(box(4, 2.5, 1, m, -1.7, -7.5, -0.5));
      g.add(box(3, 7, 1, m, 2.2, -4, -0.5));
      g.add(box(4, 2.5, 1, m, 2.7, -7.5, -0.5));
      g.add(box(1.2, 2.4, 1.6, peg, 0, 0.4, -0.3));
      break;
    case 2: // a towel folded over the line
      g.add(box(10, 12, 1.4, m, 0, -6, -0.2));
      g.add(box(10, 1, 1.6, mat(0xf4efe3), 0, -9, -0.2));
      break;
    default: // trousers by the waist
      g.add(box(8, 3, 1, m, 0, -1.5, -0.5));
      g.add(box(3.4, 9, 1, m, -2.3, -7.5, -0.5));
      g.add(box(3.4, 9, 1, m, 2.3, -7.5, -0.5));
      g.add(box(1.2, 2.4, 1.6, peg, -2.5, 0.4, -0.3));
      g.add(box(1.2, 2.4, 1.6, peg, 2.5, 0.4, -0.3));
      break;
  }
  addOutlines(g, 0.3);
  return g;
}

export function randomLaundryColour(): number {
  return LAUNDRY_COLOURS[Math.floor(Math.random() * LAUNDRY_COLOURS.length)];
}
